import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';

import { StyledDivider } from './Sidebar.styles';
import { NavGroup, NavItem } from './Sidebar.types';
import SidebarNavGroup from './SidebarNavGroup';
import { getNavItemKey } from './SidebarNavItem';

interface SidebarNavListProps {
  groups: NavGroup[];
  isCollapsed: boolean;
  currentPath: string;
  currentSearch: string;
  onNavigate?: () => void;
}

const matchesPath = (item: NavItem, currentPath: string): boolean => {
  if (!item.path) return false;
  if (item.path === '/') return currentPath === '/';
  return currentPath === item.path || currentPath.startsWith(`${item.path}/`);
};

const matchesSearchParams = (searchParams: Record<string, string | null>, currentParams: URLSearchParams): boolean =>
  Object.entries(searchParams).every(([key, value]) => currentParams.get(key) === value);

const getActiveItemKey = (group: NavGroup, currentPath: string, currentSearch: string): string | null => {
  const currentParams = new URLSearchParams(currentSearch);
  const pathItems = group.items.filter((item) => matchesPath(item, currentPath));
  const activeItem =
    pathItems.find((item) => item.searchParams && matchesSearchParams(item.searchParams, currentParams)) ??
    pathItems.find((item) => !item.searchParams);
  return activeItem ? getNavItemKey(activeItem) : null;
};

const SidebarNavList: FC<SidebarNavListProps> = ({ groups, isCollapsed, currentPath, currentSearch, onNavigate }) => {
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const currentPathRef = useRef(currentPath);

  useEffect(() => {
    currentPathRef.current = currentPath;
    setPendingKey(null);
  }, [currentPath, currentSearch]);

  const handleNavigate = useCallback(
    (itemKey: string) => {
      setPendingKey(itemKey);
      onNavigate?.();
    },
    [onNavigate]
  );

  return (
    <Box component="nav" sx={{ pb: 2, pt: 1 }}>
      {groups.map((group, index) => {
        const routeActiveKey = getActiveItemKey(group, currentPath, currentSearch);
        const effectiveActiveKey =
          pendingKey && group.items.some((item) => item.path && getNavItemKey(item) === pendingKey)
            ? pendingKey
            : routeActiveKey;

        return (
          <Box key={group.label}>
            {index > 0 && <StyledDivider />}
            <SidebarNavGroup
              group={group}
              isCollapsed={isCollapsed}
              activeItemKey={effectiveActiveKey}
              onNavigate={handleNavigate}
              onClose={onNavigate}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default SidebarNavList;
